import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {
    AuditInfoDto,
    DataElementAdditionCor1Dto,
    DataElementAdditionCor2Dto,
    DataElementDto,
    DataElementTypeDto,
    StorageTransferDataDto,
    StorageTransferDto,
    StorageTransferFinishDto,
    StorageTransferReasonDto,
    StorageTransferStatusDto
} from "../../api/sts";
import {stsCustomerStore} from "./StsCustomerStore";

const systemApi = api.sts.api

interface ListItem {
    documentType: DataElementTypeDto
    documentId: string
    addition: string
}

function dataElementToListItem(item: DataElementDto): ListItem {
    return {
        documentType: item.type,
        documentId: item.documentId,
        addition: (item.type === DataElementTypeDto.COR_I ? (item.addition as DataElementAdditionCor1Dto).corSerial : (
            item.type === DataElementTypeDto.COR_II ? (item.addition as DataElementAdditionCor2Dto).corNumber : ''
        )) || ''
    }
}

class StsDetailStore {
    get updating(): boolean {
        return this._updating;
    }

    private set updating(value: boolean) {
        this._updating = value;
    }

    get customerDisplay(): string | undefined {
        return this._customerDisplay;
    }

    private set customerDisplay(value: string | undefined) {
        this._customerDisplay = value;
    }

    get audit(): AuditInfoDto | undefined {
        return this._audit;
    }

    private set audit(value: AuditInfoDto | undefined) {
        this._audit = value;
    }

    get data(): StorageTransferDataDto | undefined {
        return this._data
    }

    private set data(value: StorageTransferDataDto | undefined) {
        this._data = value
    }

    get id(): string {
        return this._id
    }

    set id(value: string) {
        this._id = value
    }

    get vin(): string {
        return this.data?.vin || '-'
    }

    get isSca(): boolean {
        return this.data?.reason === StorageTransferReasonDto.SECURITY_CODE_ACQUISITION
    }

    get listItems(): ListItem[] {
        return (this.data?.items || []).map(item => dataElementToListItem(item))
    }

    get printerOrCabinetUpdatable(): boolean {
        if (this.data?.status !== StorageTransferStatusDto.PLACEMENT_READY) {
            return false
        }

        const {
            labelPrinterId,
            destinationCabinet
        } = this.data || {}


        return !labelPrinterId || !destinationCabinet;
    }

    private _id: string = ''
    private _customerDisplay: string | undefined
    private _data: StorageTransferDataDto | undefined
    private _audit: AuditInfoDto | undefined
    private _updating: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    async load(): Promise<StorageTransferDataDto | undefined> {
        const {
            id
        } = this
        const response = await catchApi(async () => await systemApi.getStorageTransfer(id))

        return this.process(response?.data)?.data
    }

    async update(dto: StorageTransferFinishDto): Promise<any> {
        const {id} = this
        this.updating = true
        const response = await catchApi(async () => systemApi.finishStorageTransfer(id, dto))
        return this.process(response?.data)
    }

    private process(transfer: StorageTransferDto | undefined): StorageTransferDto | undefined {
        const data = transfer?.data
        const found = stsCustomerStore.items.find(e => e.id === data?.customerId)
        if (found) {
            this.customerDisplay = `${found.label}`
        } else {
            this.customerDisplay = undefined
        }
        this.audit = transfer?.audit
        this.data = data
        return transfer
    }
}

const stsDetailStore = new StsDetailStore()

export {
    stsDetailStore
}
